import React from 'react';
import styled from 'styled-components/macro';
import { sizesMap, textLineHeight } from '../../typography/utils';
import { SPACINGS } from '../../utils';
import { Grid } from '../../atoms';
import { motion } from 'framer-motion';

const TabsMenuStyled = styled.div`
  display: block;
  position: relative;
  background-color: ${(p) => p.theme.colors.secondary95w};
  padding: ${SPACINGS[1]};
`;

type ItemStyledProps = {
  size: 'sm' | 'base';
  selected: boolean;
};
const ItemStyled = styled.button<ItemStyledProps>`
  outline: 0;
  padding: ${SPACINGS[5]} 0;
  width: 100%;
  border: 1px solid transparent;
  background-color: transparent;

  color: ${(props) => props.theme.colors.black};
  font-family: ${(props) => props.theme.fonts.gtEesti};
  font-size: ${sizesMap.sm.size};
  line-height: ${textLineHeight};
  text-transform: uppercase;
  font-weight: 300;
  letter-spacing: 0.02em;
  opacity: ${(props) => (props.disabled ? '0.3' : 1)};

  cursor: pointer;
  &:disabled {
    cursor: not-allowed;
  }

  &:not(:disabled) {
    &:hover,
    &:focus {
      outline: none;
      color: ${(props) => props.theme.colors.primary};
    }
  }
`;

const SelectedMarker = styled(motion.div)<{ widthPercent: number }>`
  position: absolute;
  margin-left: ${SPACINGS[1]};
  height: calc(100% - ${SPACINGS[2]});
  width: calc(${(p) => p.widthPercent}% - ${SPACINGS[2]});
  border: 1px solid ${(p) => p.theme.colors.black80w};
  background-color: ${(p) => p.theme.colors.white};
  z-index: 1;
  border-radius: 10px;
`;

type ItemWithParams = {
  disabled?: boolean;
  hidden?: boolean;
};

type ButtonTabMenuProps<I> = {
  items: I[];
  selectedItem: I;
  keyAccessor: (i: I) => string;
  labelAccessor: (i: I) => string | React.ReactNode;
  onChange: (i: I) => void;
  size?: 'sm' | 'base';
  style?: React.CSSProperties;
};

function ButtonTabMenu<I extends ItemWithParams>(props: ButtonTabMenuProps<I>) {
  const {
    items,
    selectedItem,
    keyAccessor,
    labelAccessor,
    onChange,
    size = 'base',
    style,
  } = props;

  const visibleItems = items.filter((i) => !i.hidden);
  const selectedIndex = visibleItems.findIndex(
    (i) => keyAccessor(selectedItem) === keyAccessor(i)
  );
  const percent = 100 / visibleItems.length;

  return (
    <TabsMenuStyled style={style}>
      <Grid.Row gutters={SPACINGS[1]}>
        <SelectedMarker
          widthPercent={percent}
          initial={{ left: `${selectedIndex * percent}%` }}
          animate={{ left: `${selectedIndex * percent}%` }}
          transition={{
            left: { type: 'spring', stiffness: 300, damping: 30 },
          }}
        />
        {visibleItems.map((item) => (
          <Grid.Col key={keyAccessor(item)} style={{ zIndex: 2 }}>
            <ItemStyled
              size={size}
              disabled={item.disabled}
              selected={keyAccessor(item) === keyAccessor(selectedItem)}
              key={keyAccessor(item)}
              onClick={() => onChange(item)}
            >
              {labelAccessor(item)}
            </ItemStyled>
          </Grid.Col>
        ))}
      </Grid.Row>
    </TabsMenuStyled>
  );
}

export default ButtonTabMenu;
